import {
  BenefitsEnrollmentTransitionEventEnum,
  BenefitsEnrollmentStateEnum,
  BenefitsEnrollmentCancellationReasonEnum,
  BenefitsEnrollment,
  BenefitsDependentEnrollment,
} from "@oysterhr/schema"
import { useTranslation } from "react-i18next"
import { EnrollmentStatePill } from "../EnrollmentStatePill"
import { FinishReasonModal, FinishReasonFormValues } from "../FinishReasonModal"
import { MouseEvent, useState } from "react"
import { Button, Text } from "@oysterhr/ui"
import { DateTime } from "luxon"
import { ConfirmModal } from "~/shared/components/ConfirmModal"
import { useTransitionEnrollment } from "../../hooks/useTransitionEnrollment"
import { useTransitionDependentEnrollment } from "../../hooks/useTransitionDependentEnrollment"
import { TransitionToEnrolledModal } from "../TransitionToEnrolledModal"
import { EnrollmentCancellationConfirmModal } from "../EnrollmentCancellationConfirmModal"

export interface EnrollmentStateActionPillProps {
  benefitEnrollment: BenefitsEnrollment | BenefitsDependentEnrollment
  parentEnrollmentPriceCurrency?: string
  onStateChange: () => void
  onStateFail: (errors: Array<string>, enrollmentId: string) => void
  editable: boolean
}

const dateLike1Jan2000 = (isoDate: string): string =>
  DateTime.fromISO(isoDate).toLocaleString(DateTime.DATE_MED)

const { Finish, Cancel, OptOut, DenyBenefit, Enroll } = BenefitsEnrollmentTransitionEventEnum
const IN_PROGRESS_CANCELLATION_TRANSITIONS = new Set([Cancel, OptOut, DenyBenefit])
const { AwaitingToBeFinished } = BenefitsEnrollmentStateEnum
interface TransitionEnrollmentHandlerProps {
  event: BenefitsEnrollmentTransitionEventEnum
  values?: FinishReasonFormValues
}

export function EnrollmentStateActionPill({
  benefitEnrollment,
  onStateChange,
  onStateFail,
  editable = false,
  parentEnrollmentPriceCurrency,
}: EnrollmentStateActionPillProps) {
  const { t } = useTranslation("benefits/EnrollmentState")

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isFinishReasonModalOpen, setIsFinishReasonModalOpen] = useState(false)
  const [isEnrollmentModalOpen, setIsEnrollmentModalOpen] = useState(false)
  const [inPorgressCancellationTransition, setInProgressCancellationTransition] =
    useState<BenefitsEnrollmentTransitionEventEnum | null>(null)
  const [effectiveDate, setEffectiveDate] = useState("")

  const {
    __typename,
    id,
    availableEvents,
    state,
    coverageEndDate,
    cancellationReason,
    coverageStartDate,
  } = benefitEnrollment

  const isDependentEnrollment = __typename === "BenefitsDependentEnrollment"

  const [transitionEnrollment] = useTransitionEnrollment({
    onCompleted: data => {
      if (data.transitionEnrollment.errors?.length === 0) {
        onStateChange()
        const coverageEndDate = data?.transitionEnrollment.benefitEnrollment?.coverageEndDate
        setEffectiveDate(coverageEndDate)
      }
    },
    setPayloadErrorsHook: errors => onStateFail(errors, id),
  })

  const [transitionDependentEnrollment] = useTransitionDependentEnrollment({
    onCompleted: data => {
      if (data.transitionDependentEnrollment.errors?.length === 0) {
        onStateChange()
        const coverageEndDate =
          data?.transitionDependentEnrollment.dependentEnrollment?.coverageEndDate
        setEffectiveDate(coverageEndDate)
      }
    },
    setPayloadErrorsHook: errors => onStateFail(errors, id),
  })

  const transitionBenefitsEnrollmentHandler = async ({
    event,
    values,
  }: TransitionEnrollmentHandlerProps) => {
    await transitionEnrollment({
      variables: {
        enrollmentId: id,
        transitionEvent: event,
        coverageEndDate: values?.coverageEndDate,
        cancellationReason: values?.cancellationReason as BenefitsEnrollmentCancellationReasonEnum,
      },
    })
  }

  const transitionDependentEnrollmentHandler = async ({
    event,
    values,
  }: TransitionEnrollmentHandlerProps) => {
    await transitionDependentEnrollment({
      variables: {
        dependentEnrollmentId: id,
        transitionEvent: event,
        coverageEndDate: values?.coverageEndDate,
        cancellationReason: values?.cancellationReason as BenefitsEnrollmentCancellationReasonEnum,
      },
    })
  }

  const transitionEnrollmentHandler = isDependentEnrollment
    ? transitionDependentEnrollmentHandler
    : transitionBenefitsEnrollmentHandler

  const generateEnrollmentStateEvents = () => {
    const events = state === AwaitingToBeFinished ? [Finish] : availableEvents

    return events?.map(event => ({
      title: t(`transitions.${state === AwaitingToBeFinished ? state : event}`), // AwaitingToBeFinsihed state translation is edit cancellation details
      onClick: () => {
        if (event === Finish) {
          setIsFinishReasonModalOpen(true)
          return
        }

        if (event === Enroll) {
          setIsEnrollmentModalOpen(true)
          return
        }

        if (
          benefitEnrollment.plan?.provider?.automated &&
          benefitEnrollment.state === BenefitsEnrollmentStateEnum.InProgress &&
          IN_PROGRESS_CANCELLATION_TRANSITIONS.has(event)
        ) {
          setInProgressCancellationTransition(event)
          return
        }

        if (event === Cancel) {
          setIsCancelModalOpen(true)
          return
        }
        transitionEnrollmentHandler({ event })
      },
    }))
  }

  const handleFinishReasonSubmit = (values: FinishReasonFormValues) => {
    setIsFinishReasonModalOpen(false)
    transitionEnrollmentHandler({ event: Finish, values })
  }

  const handleCancellationConfirmation = async () => {
    await transitionEnrollmentHandler({ event: Cancel })
    setIsCancelModalOpen(false)
  }

  const handleInProgressCancellationOfAutomatedEnrollment = () => {
    transitionEnrollmentHandler({
      event: inPorgressCancellationTransition as BenefitsEnrollmentTransitionEventEnum,
    })
    setInProgressCancellationTransition(null)
  }

  const dropdownValues = !editable ? [] : generateEnrollmentStateEvents()

  return (
    <>
      <Button
        className="hidden"
        onClick={(ev: MouseEvent<HTMLButtonElement>) => ev.stopPropagation()}
      >
        <TransitionToEnrolledModal
          onSuccess={onStateChange}
          parentEnrollmentPriceCurrency={parentEnrollmentPriceCurrency}
          enrollment={benefitEnrollment}
          isOpen={isEnrollmentModalOpen}
          onClose={() => setIsEnrollmentModalOpen(false)}
        />
        <FinishReasonModal
          initialValues={{
            enrollmentId: id,
            coverageEndDate: coverageEndDate,
            cancellationReason: cancellationReason || "",
          }}
          isOpen={isFinishReasonModalOpen}
          onClose={() => setIsFinishReasonModalOpen(false)}
          onSubmit={handleFinishReasonSubmit}
        />
        <EnrollmentCancellationConfirmModal
          isVisible={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancellationConfirmation}
        />
        <ConfirmModal
          dataTestId="cancelled-confirmation-modal"
          visible={!!effectiveDate}
          onClose={() => setEffectiveDate("")}
          title={t("cancelledConfirmation.title")}
          cancelLabel={t("cancelledConfirmation.close")}
        >
          <Text>
            {t("cancelledConfirmation.message", {
              effectiveDate: dateLike1Jan2000(effectiveDate),
            })}
          </Text>
        </ConfirmModal>
        <ConfirmModal
          dataTestId="in-progress-cancelled-modal"
          visible={!!inPorgressCancellationTransition}
          onClose={() => setInProgressCancellationTransition(null)}
          onOk={handleInProgressCancellationOfAutomatedEnrollment}
          title={t("inProgressCancelConfirmation.title")}
        >
          <Text>{t("inProgressCancelConfirmation.message")}</Text>
        </ConfirmModal>
      </Button>
      <EnrollmentStatePill
        data-testid="state-pill"
        state={state}
        values={dropdownValues}
        startDate={coverageStartDate}
      />
    </>
  )
}
